import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://ebrhcpikcacxqqkalkmd.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVicmhjcGlrY2FjeHFxa2Fsa21kIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzUxMzY3ODcsImV4cCI6MjA5MDcxMjc4N30.h018LgbI4Uj1UzJQUm2cws76NV7BGMqTNb9c0iOqJGs';

// Check if we're using placeholder values
const isPlaceholder = supabaseUrl === 'https://placeholder.supabase.co' ||
                     supabaseUrl === 'your_supabase_project_url' ||
                     !supabaseUrl.startsWith('http');

if (isPlaceholder) {
  console.warn(
    '⚠️ Supabase is not configured! Please update your .env file with actual Supabase credentials.\n' +
    'You can create a free project at https://supabase.com'
  );
}

// Extract project ref from the configured URL for stale session detection
const getProjectRef = (url: string): string => {
  try {
    const hostname = new URL(url).hostname;
    return hostname.split('.')[0];
  } catch {
    return '';
  }
};

const currentProjectRef = getProjectRef(supabaseUrl);

const isValidStoredSession = (value: string | null, expectedProjectRef: string): boolean => {
  if (!value) {
    return false;
  }

  try {
    const parsed = JSON.parse(value);

    if (!parsed || typeof parsed !== 'object') {
      return false;
    }

    const accessToken = typeof parsed.access_token === 'string' ? parsed.access_token : '';
    const refreshToken = typeof parsed.refresh_token === 'string' ? parsed.refresh_token : '';
    const tokenType = typeof parsed.token_type === 'string' ? parsed.token_type : '';
    const user = parsed.user && typeof parsed.user === 'object' ? parsed.user : null;
    const aud = typeof user?.aud === 'string' ? user.aud : '';
    const sessionProjectRef =
      typeof user?.app_metadata?.iss === 'string'
        ? getProjectRef(user.app_metadata.iss)
        : '';

    return Boolean(
      accessToken &&
      refreshToken &&
      tokenType &&
      aud &&
      (!expectedProjectRef || !sessionProjectRef || sessionProjectRef === expectedProjectRef)
    );
  } catch {
    return false;
  }
};

// Clear stale or malformed sessions from localStorage to prevent token refresh errors.
// This happens when localStorage contains a session from a previously configured project URL
// or a corrupted/expired payload for the current project.
const clearStaleSession = () => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      // Supabase stores sessions under keys like "sb-<project-ref>-auth-token"
      if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
        const storedRef = key.replace('sb-', '').replace('-auth-token', '');
        const storedValue = localStorage.getItem(key);
        const isCurrentProject = storedRef === currentProjectRef;
        const isValidForProject = isValidStoredSession(storedValue, currentProjectRef);

        if (storedRef && storedRef !== currentProjectRef) {
          keysToRemove.push(key);
          console.warn(`🧹 Removing stale session for project: ${storedRef} (current: ${currentProjectRef})`);
          continue;
        }

        if (isCurrentProject && !isValidForProject) {
          keysToRemove.push(key);
          console.warn(`🧹 Removing invalid persisted auth session for current project: ${currentProjectRef}`);
        }
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (err) {
    console.warn('Could not clear stale sessions from localStorage:', err);
  }
};

// Run stale session cleanup before initializing the client
if (!isPlaceholder && currentProjectRef) {
  clearStaleSession();
}

// Create a custom fetch wrapper with better error handling
const customFetch: typeof fetch = async (input, init) => {
  try {
    const response = await fetch(input, init);

    if (!response.ok && response.status !== 404 && response.status !== 406 && response.status !== 409) {
      console.error('❌ Response not OK:', {
        status: response.status,
        statusText: response.statusText,
        url: input
      });
    }

    return response;
  } catch (error) {
    console.error('🔥 Fetch error:', {
      error,
      url: input,
      headers: init?.headers,
      method: init?.method
    });

    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      console.error(
        '🚫 Network/CORS Error - Possible causes:\n' +
        '1. Supabase project is paused or down\n' +
        '2. Network connectivity issues\n' +
        '3. CORS not properly configured\n' +
        '4. Invalid Supabase URL'
      );
    }

    throw error;
  }
};

export const supabase = createClient(
  isPlaceholder ? 'https://placeholder.supabase.co' : supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
    global: {
      fetch: customFetch,
    },
  }
);
